/** One reply. A message with no text has no bubble — the store never makes one. */

import Markdown from './Markdown';
import ErrorBoundary from '../app/ErrorBoundary';
import { cn } from '../lib/cn';
import { stripEmoji as plain } from '../lib/emoji';
import type { Bubble as BubbleModel } from '../store/chatStore';
import { useSession } from '../store/sessionStore';

/** `label`: the design's «Ответ» over the first reply of a turn (`Thread.tsx`
 *  decides which reply that is). */
export default function Bubble({ bubble, label }: { bubble: BubbleModel; label?: string }) {
  const debug = useSession((state) => state.debug);
  const isGuide = bubble.role === 'guide';

  // A navigation event travels as the visitor's message — it has to, the guide
  // reads it as one — but it is not something they typed, so it is drawn as a
  // pill. After a reload it comes back as an ordinary bubble, on purpose: the
  // label is local state and inferring it from the sentence would be a reader
  // keyed on prose (`chatStore.Bubble.kind`).
  if (bubble.kind === 'nav') {
    return (
      <p className="nav-pill">
        {plain(bubble.text)}
      </p>
    );
  }

  return (
    <div
      className={cn(
        'bubble',
        isGuide ? 'bubble--guide' : 'bubble--user',
        // A peterhof_duo bridge line: same bubble shape, just a quieter voice
        // (`chat.css`) — no border, no badge, so a fast reader still sees one
        // guide speaking, not two.
        bubble.bridge ? 'bubble--bridge' : null,
        // Queued while the previous turn was still streaming
        // (`chat/sendQueue.ts`) — on screen already, not sent yet.
        bubble.pending ? 'bubble--pending' : null,
      )}
    >
      {isGuide && debug && bubble.reasoning ? (
        // Reasoning is a debugging surface only: on the public stand the
        // visitor sees the activity line instead, never the model's notes.
        <details className="bubble__reasoning">
          <summary>рассуждение</summary>
          <pre>{bubble.reasoning}</pre>
        </details>
      ) : null}
      <div className="bubble__body">
        {label ? <p className="bubble__label">{label}</p> : null}
        {isGuide ? (
          <ErrorBoundary label="markdown" fallback={<p>{plain(bubble.text)}</p>}>
            <Markdown text={bubble.text} />
          </ErrorBoundary>
        ) : (
          <p>{bubble.text}</p>
        )}
        {bubble.streaming ? <span className="bubble__caret" aria-hidden="true" /> : null}
      </div>
      {bubble.pending ? <span className="bubble__pending-caption">ожидает…</span> : null}
    </div>
  );
}
